import React, { useContext } from 'react'

import { faBan, faClock, faFile, faPaperPlane, faRocketLaunch } from '@fortawesome/pro-regular-svg-icons'
import { Box, Checkbox, ListItem, ListItemText, ListSubheader } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Dropdown, SelectableIconChips } from '@scoir/college-search'

import { ONE_TIME } from 'routes/outreach/outreach-message-types'

import OutreachMessageContext from '../OutreachMessageProvider/Context'

const useStyles = makeStyles((theme) => ({
  nested: {
    paddingLeft: theme.spacing(6),
  },
}))

const Filter = () => {
  const classes = useStyles()
  const { route, dispatch, activeView } = useContext(OutreachMessageContext)
  const { status, audience, trigger } = activeView?.[route]?.filterModel || {}
  const filterModal = activeView?.[route]?.filterModel

  const handleClear = () => {
    dispatch({ type: 'clear-filters' })
  }

  /** Audience Filter */
  const audienceOptions = [
    {
      label: 'Students',
      value: 'Student',
      ...(route === ONE_TIME
        ? {
            subItems: [
              { label: 'Followers', value: 'Followers' },
              { label: 'My Matches', value: 'MyMatches' },
              { label: 'List of Student IDs', value: 'StudentIds' },
            ],
          }
        : {}),
    },
    {
      label: 'Parents',
      value: 'Parent',
    },
  ]

  const getAudienceChecked = (value: string) => {
    if (value === 'Student') {
      const studentsChecked =
        audience?.includes('Followers') && audience?.includes('MyMatches') && audience?.includes('StudentIds')
      return Boolean(studentsChecked)
    }
    return Boolean(audience?.includes(value))
  }
  const getAudienceIndeterminate = () => {
    if (!getAudienceChecked('Student')) {
      // All student audiences are not selected
      // Check if any student audiences are selected
      return audience?.includes('Followers') || audience?.includes('MyMatches') || audience?.includes('StudentIds')
    }
    return false
  }

  const handleAudienceFilter = (value: string) => {
    if (value === 'Student') {
      if (getAudienceIndeterminate() || getAudienceChecked('Student')) {
        // Some or all student audiences are selected, remove all
        const removeStudentAudiences =
          audience?.filter(
            (audience: string) => audience !== 'Followers' && audience !== 'MyMatches' && audience !== 'StudentIds',
          ) || []
        dispatch({ type: 'update-filters', filters: { ...filterModal, audience: removeStudentAudiences } })
        return
      } else {
        // All audiences are unselected, select them
        dispatch({
          type: 'update-filters',
          filters: {
            ...filterModal,
            audience: [...(audience || []), 'Followers', 'MyMatches', 'StudentIds'],
          },
        })
      }
    } else if (audience?.includes(value)) {
      // Unselect audience
      dispatch({
        type: 'update-filters',
        filters: { ...filterModal, audience: audience.filter((audience: string) => audience !== value) },
      })
    } else {
      // Select audience
      dispatch({
        type: 'update-filters',
        filters: { ...filterModal, audience: [...(audience || []), value] },
      })
    }
  }

  /** Status Filter */
  const statusOptions = {
    'one-time': [
      {
        title: 'Delivered',
        faIcon: faPaperPlane,
        value: 'Delivered',
        selected: status?.includes('Delivered'),
      },
      {
        title: 'Scheduled',
        faIcon: faClock,
        value: 'Scheduled',
        selected: status?.includes('Scheduled'),
      },
      {
        title: 'Draft',
        faIcon: faFile,
        value: 'Draft',
        selected: status?.includes('Draft'),
      },
    ],
    automated: [
      {
        title: 'Live',
        faIcon: faRocketLaunch,
        value: 'Live',
        selected: status?.includes('Live'),
      },
      {
        title: 'Disabled',
        faIcon: faBan,
        value: 'Disabled',
        selected: status?.includes('Disabled'),
      },
      {
        title: 'Draft',
        faIcon: faFile,
        value: 'Draft',
        selected: status?.includes('Draft'),
      },
    ],
  }

  const handleStatusFilter = (value: string) => {
    if (status?.includes(value)) {
      // Unselect status
      dispatch({
        type: 'update-filters',
        filters: { ...filterModal, status: status.filter((status: string) => status !== value) },
      })
      return
    }
    dispatch({ type: 'update-filters', filters: { ...filterModal, status: [...(status || []), value] } })
  }

  /** Trigger Filter */
  const triggerOptions = [
    {
      label: 'On Follow',
      value: 'On-Follow',
    },
    {
      label: 'Marked Applying',
      value: 'Marked-Applying',
    },
    {
      label: 'Marked Applied',
      value: 'Marked-Applied',
    },
  ]

  const getTriggerChecked = (value: string) => {
    return Boolean(trigger?.includes(value))
  }

  const handleTriggerFilter = (value: string) => {
    if (trigger?.includes(value)) {
      // Unselect trigger
      dispatch({
        type: 'update-filters',
        filters: { ...filterModal, trigger: trigger.filter((trig: string) => trig !== value) },
      })
      return
    }
    // Select trigger
    dispatch({
      type: 'update-filters',
      filters: { ...filterModal, trigger: [...(trigger || []), value] },
    })
  }

  const getSelectionLabels = () => {
    const labels: string[] = []
    if (audience && audience.length > 0) {
      labels.push(`Audience`)
    }
    if (status && status.length > 0) {
      labels.push(`Status `)
    }
    if (route !== ONE_TIME && trigger && trigger.length > 0) {
      labels.push(`Trigger`)
    }
    return labels
  }

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gridGap={{ xs: 8, sm: 0 }}
    >
      <Dropdown
        buttonLabel="Filters"
        selectionLabels={getSelectionLabels()}
        onClear={handleClear}
        fieldLabel="Audience" // FIRST FILTER TITLE
      >
        <Box>
          <Box mx={2}>
            {audienceOptions.map((audience) => (
              <React.Fragment key={audience.label}>
                <ListItem
                  dense
                  button
                  onClick={() => handleAudienceFilter(audience.value)}
                >
                  <Checkbox
                    edge="start"
                    color="primary"
                    checked={getAudienceChecked(audience.value)}
                    indeterminate={audience.value === 'Student' ? getAudienceIndeterminate() : false}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': audience.value }}
                  />
                  <ListItemText
                    id={audience.value}
                    primary={audience.label}
                  />
                </ListItem>
                {audience.subItems?.map((item) => (
                  <ListItem
                    key={item.label}
                    className={classes.nested}
                    dense
                    button
                    onClick={() => handleAudienceFilter(item.value)}
                  >
                    <Checkbox
                      edge="start"
                      color="primary"
                      checked={getAudienceChecked(item.value)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-labelledby': item.value }}
                    />
                    <ListItemText
                      id={item.value}
                      primary={item.label}
                    />
                  </ListItem>
                ))}
              </React.Fragment>
            ))}
          </Box>
          {route !== ONE_TIME && (
            <Box>
              <ListSubheader>Trigger</ListSubheader>
              <Box mx={2}>
                {triggerOptions.map((trigger) => (
                  <React.Fragment key={trigger.label}>
                    <ListItem
                      dense
                      button
                      onClick={() => handleTriggerFilter(trigger.value)}
                    >
                      <Checkbox
                        edge="start"
                        color="primary"
                        checked={getTriggerChecked(trigger.value)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': trigger.value }}
                      />
                      <ListItemText
                        id={trigger.value}
                        primary={trigger.label}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}
          <Box>
            <ListSubheader>Status</ListSubheader>
            <Box mx={2}>
              <SelectableIconChips
                gridItemProps={{ xs: 6 }}
                onSelect={handleStatusFilter}
                options={statusOptions[route as keyof typeof statusOptions]}
              />
            </Box>
          </Box>
        </Box>
      </Dropdown>
    </Box>
  )
}
export default Filter
